import config from "../config";

/**
 * Generic safe fetch wrapper - mirrors LeaveService.jsx's convention.
 *
 * headers merges with (not replaces) config.getHeaders() - a caller passing
 * its own `headers` option would otherwise clobber the whole headers object
 * via the spread below, silently dropping Authorization. No current caller
 * here does that, but RateService.jsx's identical pattern did (see its own
 * comment) - fixed here too so the same bug can't recur.
 */
const safeFetch = async (url, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: { ...config.getHeaders(), ...options.headers },
  });

  if (!res.ok) {
    const errorText = await res.text();
    console.error("API Error:", errorText);
    let message = errorText;
    try {
      const parsed = JSON.parse(errorText);
      message = parsed.message || parsed.error || errorText;
    } catch {
      // not JSON - fall back to raw text
    }
    throw new Error(message || "API request failed");
  }

  if (res.status === 204) {
    return null;
  }

  const contentType = res.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    return null;
  }
  return res.json();
};

/** Paginated, filterable review list. filters: {resortId, ratingMin, ratingMax, status, fromDate, toDate} (dates as yyyy-MM-dd) */
export const getReviews = async (filters = {}, page = 0, size = 20) => {
  const params = new URLSearchParams({ page, size });
  if (filters.resortId) params.append("resortId", filters.resortId);
  if (filters.ratingMin) params.append("ratingMin", filters.ratingMin);
  if (filters.ratingMax) params.append("ratingMax", filters.ratingMax);
  if (filters.status) params.append("status", filters.status);
  if (filters.fromDate) params.append("fromDate", filters.fromDate);
  if (filters.toDate) params.append("toDate", filters.toDate);
  return safeFetch(`${config.BASE_URL}/api/reviews/manage?${params.toString()}`);
};

export const approveReply = async (id, editedText) => {
  return safeFetch(`${config.BASE_URL}/api/reviews/manage/${id}/approve`, {
    method: "POST",
    body: JSON.stringify({ editedText }),
  });
};

export const discardDraft = async (id, reason) => {
  return safeFetch(`${config.BASE_URL}/api/reviews/manage/${id}/discard`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
};

export const regenerateDraft = async (id) => {
  return safeFetch(`${config.BASE_URL}/api/reviews/manage/${id}/regenerate`, {
    method: "POST",
  });
};

export const triggerSync = async () => {
  return safeFetch(`${config.BASE_URL}/api/reviews/manage/sync`, { method: "POST" });
};

// SUPER_ADMIN only (see ReviewManagementController.averageRating).
export const getAverageRatings = async () => {
  return safeFetch(`${config.BASE_URL}/api/reviews/manage/average-rating`);
};

// Review volume + 1-5 star / reply-status mix for one calendar period -
// backs the Overview tab. period: "WEEK" | "MONTH" | "YEAR".
export const getReviewOverview = async (resortId, period = "MONTH") => {
  const params = new URLSearchParams({ period });
  if (resortId) params.append("resortId", resortId);
  return safeFetch(`${config.BASE_URL}/api/reviews/manage/overview?${params.toString()}`);
};

// ---------------- Guest-submitted, on-site reviews (Vintara's own) ----------------
//
// Entirely separate pipeline from everything above (which is all the
// Google Business Profile sync/reply flow) - see the backend's GuestReview
// entity javadoc. Moderation only: list + approve/reject. filters:
// {resortId, status} where status is "PENDING" | "APPROVED" | "REJECTED".

export const getGuestReviews = async (filters = {}, page = 0, size = 20) => {
  const params = new URLSearchParams({ page, size });
  if (filters.resortId) params.append("resortId", filters.resortId);
  if (filters.status) params.append("status", filters.status);
  return safeFetch(`${config.BASE_URL}/api/admin/guest-reviews?${params.toString()}`);
};

export const approveGuestReview = async (id) => {
  return safeFetch(`${config.BASE_URL}/api/admin/guest-reviews/${id}/approve`, { method: "PATCH" });
};

export const rejectGuestReview = async (id) => {
  return safeFetch(`${config.BASE_URL}/api/admin/guest-reviews/${id}/reject`, { method: "PATCH" });
};

export const setResortGbpLocation = async (resortId, gbpLocationId) => {
  const params = new URLSearchParams();
  if (gbpLocationId) params.append("gbpLocationId", gbpLocationId);
  return safeFetch(`${config.BASE_URL}/api/resorts/${resortId}/gbp-location?${params.toString()}`, {
    method: "PUT",
  });
};
